// src/utils/tactic/playbook.js
// ─────────────────────────────────────────────────────────────────────────
// The playbook document: what a coach draws, animates and saves.
//
// Positions live on KEYFRAMES rather than on the token, so a token is a path
// through time instead of a dot. `sampleTokenPosition` is the only reader —
// the canvas, the vision engine and the timeline all ask it where things are
// at time t, which keeps playback and editing on exactly one source of truth.
// ─────────────────────────────────────────────────────────────────────────

import { isTowerId, mirrorTowerId } from "./mapTerrain";
import { SIGHT_RADIUS } from "./visibility";

export const DEFAULT_DURATION = 30; // seconds
export const MAX_DURATION = 300;

let idCounter = 0;
/** Ids only need to be unique within a document, and must survive JSON export. */
export function makeId(prefix = "id") {
  idCounter += 1;
  return `${prefix}_${Date.now().toString(36)}_${idCounter.toString(36)}`;
}

// ── Keyframes ────────────────────────────────────────────────────────────

const byTime = (a, b) => a.t - b.t;

/** Where a token stands at time t (constant before/after its outer keys). */
export function sampleTokenPosition(token, t) {
  const keys = token?.keys;
  if (!keys || keys.length === 0) return { x: 0.5, y: 0.5 };
  if (keys.length === 1) return { x: keys[0].x, y: keys[0].y };

  if (t <= keys[0].t) return { x: keys[0].x, y: keys[0].y };
  const last = keys[keys.length - 1];
  if (t >= last.t) return { x: last.x, y: last.y };

  for (let i = 0; i < keys.length - 1; i++) {
    const a = keys[i];
    const b = keys[i + 1];
    if (t >= a.t && t <= b.t) {
      const span = b.t - a.t;
      const f = span > 0 ? (t - a.t) / span : 0;
      // Ease-in-out reads more like a real rotation than a linear slide.
      const e = f < 0.5 ? 2 * f * f : 1 - Math.pow(-2 * f + 2, 2) / 2;
      return { x: a.x + (b.x - a.x) * e, y: a.y + (b.y - a.y) * e };
    }
  }
  return { x: last.x, y: last.y };
}

/** Facing angle at time t, derived from the path — used to aim the token ring. */
export function sampleTokenHeading(token, t, dt = 0.12) {
  const a = sampleTokenPosition(token, Math.max(0, t - dt));
  const b = sampleTokenPosition(token, t + dt);
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  if (Math.abs(dx) < 1e-6 && Math.abs(dy) < 1e-6) return null;
  return Math.atan2(dy, dx);
}

const SNAP = 1e-3;

/** Insert or replace the key at time t. Returns a new keys array. */
export function upsertKeyframe(keys = [], t, x, y) {
  const next = keys.filter((k) => Math.abs(k.t - t) > SNAP);
  next.push({ t: Math.max(0, t), x, y });
  next.sort(byTime);
  return next;
}

export function removeKeyframe(keys = [], t) {
  const next = keys.filter((k) => Math.abs(k.t - t) > SNAP);
  return next.length ? next : keys; // never strip a token's last key
}

export function hasKeyframeAt(keys = [], t) {
  return keys.some((k) => Math.abs(k.t - t) <= SNAP);
}

// ── Factories ────────────────────────────────────────────────────────────

export function createHeroToken({
  heroName,
  team = "blue",
  role = "",
  x = 0.5,
  y = 0.5,
  t = 0,
} = {}) {
  return {
    id: makeId("tok"),
    kind: "hero",
    heroName: heroName || "",
    label: "",
    role,
    team,
    vision: true,
    visionRadius: SIGHT_RADIUS.hero,
    keys: [{ t, x, y }],
  };
}

export function createWardToken({ team = "blue", x = 0.5, y = 0.5, t = 0 } = {}) {
  return {
    id: makeId("tok"),
    kind: "ward",
    heroName: "",
    label: "WARD",
    role: "",
    team,
    vision: true,
    visionRadius: SIGHT_RADIUS.ward,
    keys: [{ t, x, y }],
  };
}

export function createPlaybook(overrides = {}) {
  return {
    schema: 3,
    id: makeId("pb"),
    name: "แผนใหม่",
    notes: "",
    ourSide: "blue",
    duration: DEFAULT_DURATION,
    tokens: [],
    drawings: [],
    // Towers taken down before the situation being drawn. Part of the document
    // because "this only works once their T1 top is gone" is the plan.
    downedTowers: [],
    // Named beats along the clip — "เข้าป่า", "ยึดวิชั่น", "เข้าตี". A coach
    // talks in beats, not in seconds, and the AI review reads the board at
    // exactly these moments.
    phases: [],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    ...overrides,
  };
}

// ── Drawings ─────────────────────────────────────────────────────────────
// Every mark carries a visible time window, so a rotation can be annotated
// step by step: the arrow for second 12 stays off-screen until second 12.

export function createDrawing({
  tool = "pen",
  color = "#ff1026",
  width = 3,
  points = [],
  text = "",
  tStart = 0,
  tEnd = null,
} = {}) {
  return {
    id: makeId("dr"),
    tool,
    color,
    width,
    points,
    text,
    tStart,
    tEnd, // null = stays visible to the end
  };
}

export function isDrawingVisible(d, t) {
  if (!d) return false;
  if (t < (d.tStart ?? 0)) return false;
  if (d.tEnd != null && t > d.tEnd) return false;
  return true;
}

// ── Side swap ────────────────────────────────────────────────────────────

/**
 * Mirror the whole board 180°: what was the blue half becomes the red half.
 * Team labels swap too, so "our team" keeps its colour and only the geometry
 * moves — which is what a coach means by "ถ้าเราอยู่ฝั่งเรด".
 */
export function mirrorPlaybook(playbook) {
  const flipPoint = ([x, y]) => [1 - x, 1 - y];
  const flipTeam = (team) => (team === "blue" ? "red" : team === "red" ? "blue" : team);

  return {
    ...playbook,
    ourSide: playbook.ourSide === "blue" ? "red" : "blue",
    tokens: (playbook.tokens || []).map((tok) => ({
      ...tok,
      team: flipTeam(tok.team),
      keys: (tok.keys || []).map((k) => ({ ...k, x: 1 - k.x, y: 1 - k.y })),
    })),
    drawings: (playbook.drawings || []).map((d) => ({
      ...d,
      points: (d.points || []).map(flipPoint),
    })),
    downedTowers: (playbook.downedTowers || []).map(mirrorTowerId),
    updatedAt: new Date().toISOString(),
  };
}

// ── Normalisation (imported files may be older or hand-edited) ───────────

export function normalizePlaybook(raw) {
  if (!raw || typeof raw !== "object") return null;
  const base = createPlaybook();

  const tokens = Array.isArray(raw.tokens)
    ? raw.tokens
        .map((tok) => {
          const keys = Array.isArray(tok?.keys)
            ? tok.keys
                .filter((k) => Number.isFinite(k?.x) && Number.isFinite(k?.y))
                .map((k) => ({ t: Number(k.t) || 0, x: k.x, y: k.y }))
                .sort(byTime)
            : [];
          if (!keys.length) return null;
          return {
            id: tok.id || makeId("tok"),
            kind: tok.kind || "hero",
            heroName: tok.heroName || "",
            label: tok.label || "",
            role: tok.role || "",
            team: tok.team || "blue",
            vision: tok.vision !== false,
            visionRadius: Number(tok.visionRadius) || SIGHT_RADIUS.hero,
            keys,
          };
        })
        .filter(Boolean)
    : [];

  const drawings = Array.isArray(raw.drawings)
    ? raw.drawings
        .filter((d) => Array.isArray(d?.points))
        .map((d) => ({
          id: d.id || makeId("dr"),
          tool: d.tool || "pen",
          color: d.color || "#ff1026",
          width: Number(d.width) || 3,
          points: d.points.filter(
            (p) => Array.isArray(p) && Number.isFinite(p[0]) && Number.isFinite(p[1])
          ),
          text: d.text || "",
          tStart: Number(d.tStart) || 0,
          tEnd: d.tEnd == null ? null : Number(d.tEnd),
        }))
    : [];

  return {
    ...base,
    id: raw.id || base.id,
    name: raw.name || "แผนที่นำเข้า",
    notes: raw.notes || "",
    ourSide: raw.ourSide === "red" ? "red" : "blue",
    duration: Math.min(MAX_DURATION, Math.max(5, Number(raw.duration) || DEFAULT_DURATION)),
    tokens,
    drawings,
    downedTowers: Array.isArray(raw.downedTowers)
      ? [...new Set(raw.downedTowers.filter(isTowerId))]
      : [],
    phases: Array.isArray(raw.phases)
      ? raw.phases
          .filter((p) => Number.isFinite(Number(p?.t)))
          .map((p) => ({
            id: p.id || makeId("ph"),
            t: Math.max(0, Number(p.t)),
            label: String(p.label || "").slice(0, 40),
          }))
          .sort(byTime)
      : [],
    createdAt: raw.createdAt || base.createdAt,
    updatedAt: raw.updatedAt || base.updatedAt,
  };
}

/** Longest keyframe time, so the timeline can offer to fit the duration. */
export function playbookExtent(playbook) {
  let max = 0;
  for (const tok of playbook?.tokens || []) {
    for (const k of tok.keys || []) max = Math.max(max, k.t);
  }
  for (const d of playbook?.drawings || []) {
    max = Math.max(max, d.tStart || 0, d.tEnd || 0);
  }
  for (const p of playbook?.phases || []) {
    max = Math.max(max, p.t || 0);
  }
  return max;
}

// ── Phases ───────────────────────────────────────────────────────────────

export function addPhase(phases = [], t, label) {
  const next = phases.filter((p) => Math.abs(p.t - t) > SNAP);
  next.push({ id: makeId("ph"), t: Math.max(0, Number(t) || 0), label: String(label || "").slice(0, 40) });
  next.sort(byTime);
  return next;
}

export function removePhase(phases = [], id) {
  return phases.filter((p) => p.id !== id);
}

/** The beat covering time t — the last one at or before it. */
export function phaseAt(phases = [], t) {
  let found = null;
  for (const p of phases) {
    if (p.t <= t + SNAP) found = p;
    else break;
  }
  return found;
}
